import { useState, useEffect, useCallback } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_rainforest-portal/artifacts/jepkgtmp_AMAZONIA%20-%20Favicon%402x.png";

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }

    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);
    if (standalone) return;

    // Check if dismissed recently (don't show for 3 days)
    const dismissed = localStorage.getItem('amazonia_install_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 3 * 24 * 60 * 60 * 1000) return;

    // iOS detection
    const ua = window.navigator.userAgent;
    const iosDevice = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIos(iosDevice);

    // Android/Chrome install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show banner after 5 seconds of browsing
      setTimeout(() => setShowBanner(true), 5000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // For iOS, show manual instructions after delay
    if (iosDevice) {
      setTimeout(() => setShowBanner(true), 8000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('amazonia_install_dismissed', Date.now().toString());
  };

  if (isStandalone || !showBanner) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-4 sm:w-[380px] z-[60] animate-in slide-in-from-bottom"
      style={{ animation: 'slideUp 0.4s ease-out' }}
    >
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      <div className="bg-white border border-black/10 shadow-2xl overflow-hidden">
        {/* Header bar */}
        <div className="h-1 bg-gradient-to-r from-[#00A5FF] via-[#E8231B] to-[#F5C400]" />

        <div className="p-4">
          <div className="flex items-start gap-3">
            <img src={LOGO_URL} alt="Amazonia" className="w-11 h-11 object-contain shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-extrabold text-[#111] leading-tight">AMAZONIA.FOOD</h3>
                  <p className="text-xs text-black/50 mt-0.5">Latin Food Delivery</p>
                </div>
                <button
                  onClick={handleDismiss}
                  className="p-1 text-black/30 hover:text-black/60 transition-colors duration-200 -mt-0.5 -mr-1"
                  aria-label="Fermer"
                >
                  <X size={16} />
                </button>
              </div>

              <p className="text-xs text-black/60 mt-2 leading-relaxed">
                Installez l'application pour accéder rapidement au catalogue et passer vos commandes.
              </p>

              <div className="mt-3 flex items-center gap-2">
                {isIos ? (
                  <div className="flex items-center gap-2 text-xs text-[#00A5FF]">
                    <Smartphone size={14} />
                    <span>Tapez <strong>Partager</strong> puis <strong>"Sur l'écran d'accueil"</strong></span>
                  </div>
                ) : (
                  <button
                    onClick={handleInstall}
                    data-testid="install-button"
                    className="flex items-center gap-2 px-4 py-2 bg-[#00A5FF] text-white font-semibold text-xs rounded-sm hover:bg-[#0090e0] transition-colors duration-200"
                  >
                    <Download size={14} />
                    Installer l'application
                  </button>
                )}
                <button
                  onClick={handleDismiss}
                  className="px-3 py-2 text-xs text-black/40 hover:text-black/60 transition-colors duration-200"
                >
                  Plus tard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
